import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import RegisterModal from "./RegisterModal";
import LoginModal from "./LoginModal";
import TrialTermsModal from "./TrialTermsModal";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/context/CartContext";
import { Tooltip } from "./Tooltip";
import { SiteSchemaJsonLd } from "./SiteSchemaJsonLd";

const TUBEDEALER_URL = "https://tubedealer.com";

interface LayoutProps {
  children: React.ReactNode;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 700);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [loginOpen, setLoginOpen]       = useState(false);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [trialOpen, setTrialOpen]       = useState(false);
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const { itemCount } = useCart();

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLink = (
    href: string,
    label: string,
    mobile = false,
    tooltip?: string,
    accent?: "buyList" | "myShop",
  ) => {
    const active = location === href || (href !== "/" && location.startsWith(href));
    const accentColor = accent === "buyList" ? "#e8c070" : "#b58cff";
    const accentBorder = accent === "buyList" ? "rgba(232,192,112,0.55)" : "rgba(181,140,255,0.55)";
    const link = (
      <Link
        href={href}
        className={accent ? `td-nav-accent td-nav-${accent}` : undefined}
        style={{
          color: accent ? (active ? (accent === "buyList" ? "#ffe3a6" : "#d7c3ff") : accentColor) : (active ? "#e8c070" : (mobile ? "#ccc" : "#888")),
          textDecoration: "none",
          fontSize: mobile ? 16 : 14,
          fontWeight: accent || active ? 600 : 400,
          padding: mobile ? "12px 10px" : accent ? "6px 10px" : undefined,
          display: mobile ? "block" : accent ? "inline-flex" : undefined,
          border: accent ? `1px solid ${accentBorder}` : mobile ? "1px solid #1a1a1a" : undefined,
          borderRadius: accent ? 8 : undefined,
          transition: "color 0.15s, background 0.15s, border-color 0.15s, transform 0.15s",
        }}
      >
        {label}
      </Link>
    );
    if (tooltip && !mobile) {
      return <Tooltip key={href} text={tooltip}>{link}</Tooltip>;
    }
    return link;
  };

  // ── Shared auth buttons ─────────────────────────────────────────────────────
  function AuthButtons({ mobile = false }: { mobile?: boolean }) {
    const btnBase: React.CSSProperties = {
      border: "none", borderRadius: 9, fontWeight: 700,
      cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
      fontSize: mobile ? 15 : 13,
      padding: mobile ? "12px 0" : "7px 16px",
      width: mobile ? "100%" : undefined,
      display: mobile ? "block" : undefined,
      textAlign: "center",
    };

    if (user) {
      return (
        <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", gap: mobile ? 10 : 8, alignItems: mobile ? "stretch" : "center" }}>
          {/* Username pill — shows admin badge if applicable */}
          <div style={{
            padding: mobile ? "10px 0" : "5px 12px",
            background: "#1a1a1a", border: "1px solid #2a2a2a",
            borderRadius: 8, color: "#e8c070",
            fontSize: mobile ? 14 : 12, fontWeight: 600,
            textAlign: mobile ? "center" : undefined,
          }}>
            {user.isAdmin && <span style={{ color: "#f87171", marginRight: 4 }}>⚙</span>}{user.username}
          </div>
          <button
            onClick={async () => { await logout(); setMenuOpen(false); }}
            style={{ ...btnBase, background: "transparent", color: "#555", border: "1px solid #2a2a2a" }}>
            Sign Out
          </button>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", gap: mobile ? 10 : 8, alignItems: "stretch" }}>
        {/* Sign Up — seller / tool account */}
        <button
          onClick={() => { setTrialOpen(true); setMenuOpen(false); }}
          style={{ ...btnBase, background: "#4caf50", color: "#fff", border: "none", order: mobile ? 0 : 0 }}>
          Sign Up
        </button>
        <button
          onClick={() => { setLoginOpen(true); setMenuOpen(false); }}
          style={{
            ...btnBase,
            background: "transparent", color: "#ccc",
            border: "1px solid #3a3a3a",
            order: mobile ? 1 : 1,
          }}>
          Sign In
        </button>
        {/* Register as Buyer — unchanged */}
        <button
          onClick={() => { setRegisterOpen(true); setMenuOpen(false); }}
          style={{ ...btnBase, background: "#e8c070", color: "#0a0a0a", border: "none", order: mobile ? 2 : 2 }}>
          Register as Buyer
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#e0e0e0", display: "flex", flexDirection: "column" }}>
      {/* Skip navigation — first focusable element; visible only on keyboard focus */}
      <a
        href="#main-content"
        style={{
          position: "absolute", top: -9999, left: 0, zIndex: 9999,
          padding: "10px 18px", background: "#e8c070", color: "#0a0a0a",
          fontWeight: 700, fontSize: 14, textDecoration: "none",
          borderRadius: "0 0 8px 0",
        }}
        onFocus={e => { (e.currentTarget as HTMLElement).style.top = "0"; }}
        onBlur={e =>  { (e.currentTarget as HTMLElement).style.top = "-9999px"; }}
      >
        Skip to main content
      </a>
      <style>{`
        @keyframes signInPulse {
          0%   { box-shadow: 0 0 0 0 rgba(124,58,237,0.6); }
          70%  { box-shadow: 0 0 0 9px rgba(124,58,237,0); }
          100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); }
        }
      `}</style>
      {/* Nav */}
      <nav style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "rgba(10,10,10,0.97)",
        backdropFilter: "blur(12px)",
        borderBottom: menuOpen ? "none" : "1px solid #151515",
        padding: isMobile ? "0 16px" : "10px 20px",
      }}>
        <div style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          minHeight: isMobile ? 64 : undefined,
        }}>

          {/* Brand logo */}
          <Link href="/" aria-label="TubeDealer Home" style={{ textDecoration: "none", flexShrink: 0 }}>
            <picture>
              <source
                srcSet={`${import.meta.env.BASE_URL}tubedealer-logo-v2-nobg.avif`}
                type="image/avif"
              />
              <source
                srcSet={`${import.meta.env.BASE_URL}tubedealer-logo-v2-nobg.webp`}
                type="image/webp"
              />
              <img
                src={`${import.meta.env.BASE_URL}tubedealer-logo-v2-nobg.png`}
                alt="TubeDealer logo"
                width={669}
                height={608}
                fetchPriority="high"
                style={{
                  height: isMobile ? 52 : 170,
                  width: "auto",
                  display: "block",
                  margin: isMobile ? "6px 0" : undefined,
                }}
              />
            </picture>
          </Link>


          {isMobile ? (
            /* ── Mobile: sign-in pill + hamburger ── */
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <style>{`
                @keyframes signInPulse {
                  0%   { box-shadow: 0 0 0 0 rgba(124,58,237,0.6); }
                  70%  { box-shadow: 0 0 0 9px rgba(124,58,237,0); }
                  100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); }
                }
              `}</style>
              {user ? (
                <div style={{
                  padding: "5px 12px", background: "#150e1f",
                  border: "1px solid #7c3aed", borderRadius: 20,
                  color: "#e8c070", fontSize: 12, fontWeight: 700,
                  maxWidth: 130, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  👤 {user.username}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {/* Sign Up — prominent green CTA */}
                  <button
                    onClick={() => setTrialOpen(true)}
                    style={{
                      padding: "9px 15px", background: "#4caf50",
                      border: "none", borderRadius: 20,
                      color: "#fff", fontWeight: 700, fontSize: 13,
                      cursor: "pointer", fontFamily: "inherit",
                      whiteSpace: "nowrap", minHeight: 44,
                    }}
                  >
                    Sign Up
                  </button>
                  {/* Sign In — secondary */}
                  <button
                    onClick={() => setLoginOpen(true)}
                    style={{
                      padding: "9px 14px", background: "transparent",
                      border: "1px solid #3a3a3a", borderRadius: 20,
                      color: "#aaa", fontWeight: 600, fontSize: 13,
                      cursor: "pointer", fontFamily: "inherit",
                      whiteSpace: "nowrap", minHeight: 44,
                    }}
                  >
                    Sign In
                  </button>
                </div>
              )}
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                style={{
                  background: "none", border: "1px solid #2a2a2a",
                  borderRadius: 8, color: "#ccc",
                  padding: "8px 12px", cursor: "pointer",
                  fontSize: 20, lineHeight: 1, flexShrink: 0,
                  minWidth: 44, minHeight: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {menuOpen ? "✕" : "☰"}
              </button>
            </div>
          ) : (
            /* ── Desktop: inline links + CTAs ── */
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* ── Browse Available Tubes ── */}
                <Tooltip text="Browse all tubes for sale">
                  <Link
                    href="/browse"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "7px 16px", borderRadius: 10,
                      background: location === "/browse" ? "rgba(76,175,80,0.18)" : "rgba(76,175,80,0.07)",
                      border: `2px solid ${location === "/browse" ? "rgba(76,175,80,0.80)" : "rgba(76,175,80,0.45)"}`,
                      boxShadow: location === "/browse"
                        ? "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)"
                        : "0 0 10px rgba(76,175,80,0.12), 0 1px 4px rgba(0,0,0,0.3)",
                      color: location === "/browse" ? "#4caf50" : "#a0c8a2",
                      textDecoration: "none", fontSize: 15, fontWeight: 600,
                      transition: "background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s, transform 0.1s",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.background = "rgba(76,175,80,0.18)";
                      el.style.borderColor = "rgba(76,175,80,0.75)";
                      el.style.boxShadow = "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)";
                      el.style.color = "#4caf50";
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(1)";
                      if (location !== "/browse") {
                        el.style.background = "rgba(76,175,80,0.07)";
                        el.style.borderColor = "rgba(76,175,80,0.45)";
                        el.style.boxShadow = "0 0 10px rgba(76,175,80,0.12), 0 1px 4px rgba(0,0,0,0.3)";
                        el.style.color = "#a0c8a2";
                      }
                    }}
                    onMouseDown={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(0.94)";
                      el.style.boxShadow = "0 0 26px rgba(76,175,80,0.50), 0 2px 12px rgba(0,0,0,0.4)";
                      el.style.borderColor = "rgba(76,175,80,0.95)";
                      el.style.background = "rgba(76,175,80,0.28)";
                    }}
                    onMouseUp={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(1)";
                      el.style.boxShadow = "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)";
                    }}
                  >
                    Browse Available Tubes
                  </Link>
                </Tooltip>

                {/* ── Required Tubes for Specific Gear ── */}
                <Tooltip text="Look up which tubes your amp or hi-fi needs, then buy them here">
                  <Link
                    href="/gear"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "7px 16px", borderRadius: 10,
                      background: location.startsWith("/gear") ? "rgba(76,175,80,0.18)" : "rgba(76,175,80,0.07)",
                      border: `2px solid ${location.startsWith("/gear") ? "rgba(76,175,80,0.80)" : "rgba(76,175,80,0.45)"}`,
                      boxShadow: location.startsWith("/gear")
                        ? "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)"
                        : "0 0 10px rgba(76,175,80,0.12), 0 1px 4px rgba(0,0,0,0.3)",
                      color: location.startsWith("/gear") ? "#4caf50" : "#a0c8a2",
                      textDecoration: "none", fontSize: 15, fontWeight: 600,
                      transition: "background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s, transform 0.1s",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.background = "rgba(76,175,80,0.18)";
                      el.style.borderColor = "rgba(76,175,80,0.75)";
                      el.style.boxShadow = "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)";
                      el.style.color = "#4caf50";
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(1)";
                      if (!location.startsWith("/gear")) {
                        el.style.background = "rgba(76,175,80,0.07)";
                        el.style.borderColor = "rgba(76,175,80,0.45)";
                        el.style.boxShadow = "0 0 10px rgba(76,175,80,0.12), 0 1px 4px rgba(0,0,0,0.3)";
                        el.style.color = "#a0c8a2";
                      }
                    }}
                    onMouseDown={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(0.94)";
                      el.style.boxShadow = "0 0 26px rgba(76,175,80,0.50), 0 2px 12px rgba(0,0,0,0.4)";
                      el.style.borderColor = "rgba(76,175,80,0.95)";
                      el.style.background = "rgba(76,175,80,0.28)";
                    }}
                    onMouseUp={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(1)";
                      el.style.boxShadow = "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)";
                    }}
                  >
                    🔧 Required Tubes for Specific Gear
                  </Link>
                </Tooltip>

                {/* ── Cart pill ── */}
                <Tooltip text="Your shopping cart">
                  <Link
                    href="/cart"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "7px 14px", borderRadius: 10, position: "relative",
                      background: location === "/cart" ? "rgba(76,175,80,0.18)" : "rgba(76,175,80,0.07)",
                      border: `2px solid ${location === "/cart" ? "rgba(76,175,80,0.80)" : "rgba(76,175,80,0.45)"}`,
                      boxShadow: location === "/cart"
                        ? "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)"
                        : "0 0 10px rgba(76,175,80,0.12), 0 1px 4px rgba(0,0,0,0.3)",
                      textDecoration: "none",
                      transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.1s",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.background = "rgba(76,175,80,0.18)";
                      el.style.borderColor = "rgba(76,175,80,0.75)";
                      el.style.boxShadow = "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)";
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(1)";
                      if (location !== "/cart") {
                        el.style.background = "rgba(76,175,80,0.07)";
                        el.style.borderColor = "rgba(76,175,80,0.45)";
                        el.style.boxShadow = "0 0 10px rgba(76,175,80,0.12), 0 1px 4px rgba(0,0,0,0.3)";
                      }
                    }}
                    onMouseDown={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(0.94)";
                      el.style.boxShadow = "0 0 26px rgba(76,175,80,0.50), 0 2px 12px rgba(0,0,0,0.4)";
                      el.style.borderColor = "rgba(76,175,80,0.95)";
                      el.style.background = "rgba(76,175,80,0.28)";
                    }}
                    onMouseUp={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "scale(1)";
                      el.style.boxShadow = "0 0 18px rgba(76,175,80,0.30), 0 2px 8px rgba(0,0,0,0.35)";
                    }}
                  >
                    <span style={{ fontSize: 18 }}>🛒</span>
                    {itemCount > 0 && (
                      <span style={{
                        position: "absolute", top: -5, right: -5,
                        background: "#e8c070", color: "#0a0a0a",
                        borderRadius: "50%", fontSize: 9, fontWeight: 800,
                        minWidth: 14, height: 14, display: "flex",
                        alignItems: "center", justifyContent: "center",
                        padding: "0 3px",
                      }}>
                        {itemCount}
                      </span>
                    )}
                  </Link>
                </Tooltip>

                {user && (
                  <Tooltip text="Scan tube markings, look up prices & access your Inventory System">
                    <a
                      href="/tubescan/?view=upload"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "7px 14px", borderRadius: 10,
                        background: "rgba(76,175,80,0.10)", border: "1px solid rgba(76,175,80,0.35)",
                        color: "#4caf50", textDecoration: "none", fontSize: 15, fontWeight: 600,
                        transition: "background 0.15s, border-color 0.15s",
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.background = "rgba(76,175,80,0.20)";
                        (e.currentTarget as HTMLElement).style.borderColor = "rgba(76,175,80,0.65)";
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.background = "rgba(76,175,80,0.10)";
                        (e.currentTarget as HTMLElement).style.borderColor = "rgba(76,175,80,0.35)";
                      }}
                    >
                      🔬 TubeScan
                    </a>
                  </Tooltip>
                )}
                {user && navLink("/buy-list", "Buy List", false, "Post the tubes you're hunting for — sellers come to you", "buyList")}
                {user && navLink("/seller", "My Shop", false, "Your seller dashboard and storefront", "myShop")}
                {user && navLink("/support", "Support", false, "Get help or submit a support ticket")}
                {user?.isAdmin && navLink("/admin", "⚙ Admin", false, "Admin console — manage users, prices, settings & more")}
              </div>
              <AuthButtons />
            </>
          )}
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <div style={{
          position: "sticky",
          top: 64,
          zIndex: 49,
          background: "rgba(10,10,10,0.98)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid #1a1a1a",
          padding: "8px 20px 16px",
          display: "flex",
          flexDirection: "column",
        }}>
          {navLink("/browse", "Shop", true)}
          {navLink("/gear", "🔧 Gear Finder", true)}
          {navLink("/cart", `🛒 Cart${itemCount > 0 ? ` (${itemCount})` : ""}`, true)}
          {user && (
            <a
              href="/tubescan/?view=upload"
              onClick={() => setMenuOpen(false)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "12px 0", borderBottom: "1px solid #1a1a1a",
                color: "#4caf50", textDecoration: "none",
                fontSize: 15, fontWeight: 600,
              }}
            >
              🔬 TubeScan
            </a>
          )}
          {user && navLink("/buy-list", "📋 Buy List", true, undefined, "buyList")}
          {user && navLink("/seller", "My Shop", true, undefined, "myShop")}
          {user && navLink("/support", "Support", true)}
          {user?.isAdmin && navLink("/admin", "⚙ Admin Console", true)}
          {navLink("/about", "About & FAQ", true)}

          <div style={{ paddingTop: 14 }}>
            <AuthButtons mobile />
          </div>
        </div>
      )}

      {/* Site-wide structured data (Organization + WebSite JSON-LD for Google Knowledge Panel) */}
      <SiteSchemaJsonLd />

      {/* Content */}
      <main id="main-content" style={{ flex: 1 }}>
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        borderTop: "1px solid #1a1a1a",
        padding: "24px 20px",
        textAlign: "center",
        background: "#080808",
      }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 28, flexWrap: "wrap", marginBottom: 8 }}>
          <Link href="/browse" style={{ color: "#e8c070", fontSize: 13, fontWeight: 400, textDecoration: "none" }}>Browse Tubes</Link>
          <span style={{ color: "#333" }}>·</span>
          <Link href="/support" style={{ color: "#e8c070", fontSize: 13, fontWeight: 400, textDecoration: "none" }}>Contact &amp; Support</Link>
          <span style={{ color: "#333" }}>·</span>
          <a href="/api/marketplace/rss" target="_blank" rel="noopener noreferrer" style={{ color: "#e8c070", fontSize: 13, fontWeight: 400, textDecoration: "none" }}>RSS Feed</a>
          <span style={{ color: "#333" }}>·</span>
          <Link href="/terms" style={{ color: "#e8c070", fontSize: 13, fontWeight: 400, textDecoration: "none" }}>Terms & Conditions and Privacy Policy</Link>
          <span style={{ color: "#333" }}>·</span>
          <Link href="/about" style={{ color: "#e8c070", fontSize: 13, fontWeight: 400, textDecoration: "none" }}>About & FAQ</Link>
          <span style={{ color: "#333" }}>·</span>
          <Link href="/editorial-policy" style={{ color: "#e8c070", fontSize: 13, fontWeight: 400, textDecoration: "none" }}>Editorial Policy</Link>
        </div>
        <p style={{ color: "#555", fontSize: 12, margin: "0 0 14px" }}>
          TubeDealer Marketplace • Oak Park, MI
        </p>
        <div
          aria-label="TubeDealer trust highlights"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          {[
            {
              label: "Verified Tube Dealers",
              href: "/browse",
              title: "Browse listings from TubeDealer sellers",
            },
            {
              label: "Tested & Guaranteed NOS Inventory",
              href: "/browse?condition=New%20Old%20Stock",
              title: "Browse New Old Stock inventory",
            },
            {
              label: "Secure Multi-Channel Sync",
              href: "/user-guide#s16",
              title: "Learn about storefronts and marketplace integrations",
            },
          ].map((highlight) => (
            <Link
              key={highlight.label}
              href={highlight.href}
              title={highlight.title}
              style={{
                border: "1px solid #242424",
                borderRadius: 999,
                color: "#777",
                fontSize: 12,
                padding: "5px 10px",
                textDecoration: "none",
                transition: "border-color 0.15s, color 0.15s, background 0.15s",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.borderColor = "#806b43";
                event.currentTarget.style.color = "#e8c070";
                event.currentTarget.style.background = "rgba(232,192,112,0.06)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.borderColor = "#242424";
                event.currentTarget.style.color = "#777";
                event.currentTarget.style.background = "transparent";
              }}
            >
              {highlight.label}
            </Link>
          ))}
        </div>
        <p style={{ color: "#333", fontSize: 12, margin: 0 }}>
          Copyright 2000–2026{" "}
          <a href={TUBEDEALER_URL} target="_blank" rel="noopener noreferrer" aria-label="TubeDealer.com (opens in new tab)" style={{ color: "#555", textDecoration: "underline" }}>
            TubeDealer.com
          </a>
        </p>
      </footer>

      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
      />
      <RegisterModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
      {trialOpen && <TrialTermsModal onClose={() => setTrialOpen(false)} />}
    </div>
  );
}
